import { Scan } from 'lucide-react';

import { cn } from '@/lib/utils';

import { type CustomizationSettings, TYPOGRAPHY_OPTIONS } from '../schemas/customizationSchemas';

// The shared branding form, laid out as the designer's three fieldsets
// (react-new-html `CustomizationDrawer`): Font Properties, Input Field Color,
// Button Color. Used by BOTH the /customization page and the workflow editor's
// Customization drawer so the two never drift apart.
//
// Note the grouping is the designer's, not our data model's: Error / Success
// live under "Font Properties" in the UI but are still `statusColor` in the
// settings, and Typography sits in the same box as the font colours.
export type PatchCustomization = <K extends keyof CustomizationSettings>(
  key: K,
  value: Partial<CustomizationSettings[K]> | CustomizationSettings[K],
) => void;

export function CustomizationFields({
  settings,
  onPatch,
}: {
  settings: CustomizationSettings;
  onPatch: PatchCustomization;
}) {
  return (
    <>
      <FieldGroup legend="Font Properties">
        <ColorRow>
          <ColorItem
            label="Heading"
            value={settings.fontColor.heading}
            onChange={(v) => onPatch('fontColor', { heading: v })}
          />
          <ColorItem
            label="Body Text"
            value={settings.fontColor.bodyText}
            onChange={(v) => onPatch('fontColor', { bodyText: v })}
          />
          <ColorItem
            label="Error"
            value={settings.statusColor.error}
            onChange={(v) => onPatch('statusColor', { error: v })}
          />
          <ColorItem
            label="Success"
            value={settings.statusColor.success}
            onChange={(v) => onPatch('statusColor', { success: v })}
          />
        </ColorRow>
        <FieldBlock label="Typography Style">
          <select
            className="h-10 w-full cursor-pointer rounded-lg border border-grayEF bg-white px-3 text-sm text-gray30 focus:border-primary focus:outline-none focus:ring-0"
            value={settings.typographyStyle}
            onChange={(e) =>
              onPatch('typographyStyle', e.target.value as CustomizationSettings['typographyStyle'])
            }
          >
            {TYPOGRAPHY_OPTIONS.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
        </FieldBlock>
      </FieldGroup>

      <FieldGroup legend="Input Field Color">
        <ColorRow>
          <ColorItem
            label="Fill"
            value={settings.inputFieldColor.fill}
            onChange={(v) => onPatch('inputFieldColor', { fill: v })}
          />
          <ColorItem
            label="Stroke"
            value={settings.inputFieldColor.stroke}
            onChange={(v) => onPatch('inputFieldColor', { stroke: v })}
          />
          <ColorItem
            label="Label"
            value={settings.inputFieldColor.label}
            onChange={(v) => onPatch('inputFieldColor', { label: v })}
          />
          <ColorItem
            label="Placeholder"
            value={settings.inputFieldColor.placeholder}
            onChange={(v) => onPatch('inputFieldColor', { placeholder: v })}
          />
        </ColorRow>
        <FieldBlock label="Corner Radius">
          <RadiusSlider
            value={settings.inputFieldColor.radius}
            onChange={(v) => onPatch('inputFieldColor', { radius: v })}
          />
        </FieldBlock>
      </FieldGroup>

      <FieldGroup legend="Button Color">
        <ColorRow>
          <ColorItem
            label="Fill"
            value={settings.buttonColor.fill}
            onChange={(v) => onPatch('buttonColor', { fill: v })}
          />
          <ColorItem
            label="Stroke"
            value={settings.buttonColor.stroke}
            onChange={(v) => onPatch('buttonColor', { stroke: v })}
          />
          <ColorItem
            label="Text"
            value={settings.buttonColor.text}
            onChange={(v) => onPatch('buttonColor', { text: v })}
          />
        </ColorRow>
        <FieldBlock label="Corner Radius">
          <RadiusSlider
            value={settings.buttonColor.radius}
            onChange={(v) => onPatch('buttonColor', { radius: v })}
          />
        </FieldBlock>
      </FieldGroup>
    </>
  );
}

/**
 * A titled block with an optional subtitle and a divider below (unless last).
 *
 * Predates the fieldset layout above and is kept for the /customization page's
 * Logo section, which is a plain titled block rather than one of the colour
 * fieldsets.
 */
export function Section({
  title,
  subtitle,
  last,
  children,
}: {
  title: string;
  subtitle?: string;
  last?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={cn('mb-4 pb-4', !last && 'border-b border-grayEF', last && 'mb-0 pb-0')}>
      <h3 className="m-0 mb-1 text-base font-bold text-black-logo">{title}</h3>
      {subtitle && <p className="m-0 mb-4 text-sm leading-relaxed text-[#303030]">{subtitle}</p>}
      {children}
    </div>
  );
}

/** Bordered box with a legend — the designer's `.sc-cust-drawer__fieldset`. */
export function FieldGroup({ legend, children }: { legend: string; children: React.ReactNode }) {
  return (
    <fieldset className="m-0 flex min-w-0 flex-col gap-4 rounded-xl border border-placeholder p-4">
      <legend className="px-2 text-base font-medium text-black-logo">{legend}</legend>
      {children}
    </fieldset>
  );
}

/** Label above a control, the designer's `.sc-cust-drawer__field-block`. */
export function FieldBlock({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2.5">
      <span className="text-sm font-medium text-gray30">{label}</span>
      {children}
    </div>
  );
}

// Four colours per row on wide panels, two when the panel is narrow.
function ColorRow({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">{children}</div>;
}

/**
 * One colour: name, a 16px swatch that opens the native picker, and the hex.
 *
 * The `<input type="color">` is layered invisibly over the swatch rather than
 * hidden and triggered by JS, so the whole swatch is a real, keyboard-reachable
 * colour control.
 */
function ColorItem({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <span className="text-xs text-gray30">{label}</span>
      <div className="flex items-center gap-2">
        <span className="relative inline-flex h-4 w-4 shrink-0">
          <span
            className="block h-full w-full rounded border border-black/25"
            style={{ backgroundColor: value }}
          />
          <input
            type="color"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            aria-label={`${label} colour`}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          />
        </span>
        <span className="truncate font-mono text-sm font-medium text-black">
          {value.toUpperCase()}
        </span>
      </div>
    </div>
  );
}

/** 0–24px corner-radius slider (designer's `.sc-cust-drawer__radius-row`). */
function RadiusSlider({
  value,
  onChange,
  className,
}: {
  value: number;
  onChange: (v: number) => void;
  className?: string;
}) {
  const max = 24;
  return (
    <div
      className={cn(
        'flex w-full items-center gap-3 rounded-lg border border-grayEF bg-white px-4 py-2.5',
        className,
      )}
    >
      <Scan size={16} className="shrink-0 text-primary" />
      <input
        type="range"
        min={0}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label="Corner radius"
        className="sc-radius-slider"
        style={{ '--progress': `${(value / max) * 100}%` } as React.CSSProperties}
      />
      <span className="shrink-0 whitespace-nowrap text-sm font-medium text-black tabular-nums">
        {value} px
      </span>
    </div>
  );
}
